import {
	createEffect,
	createMemo,
	createSignal,
	on,
	onCleanup,
} from "solid-js";

type VisiblePollOptions = {
	enabled: () => boolean;
	refetch: () => unknown;
	/** A tick is skipped while the previous request is still in flight. */
	loading?: () => boolean;
	intervalMs: number;
};

/**
 * Refetches on an interval while `enabled`. A hidden tab makes no requests,
 * so a forgotten tab does not keep loading the API; coming back to the tab
 * refetches at once, because the data went stale while it was hidden.
 */
export function createVisiblePoll(options: VisiblePollOptions): void {
	const [hidden, setHidden] = createSignal(document.hidden);
	const onVisibility = () => setHidden(document.hidden);
	document.addEventListener("visibilitychange", onVisibility);
	onCleanup(() =>
		document.removeEventListener("visibilitychange", onVisibility),
	);

	const tick = () => {
		if (!options.loading?.()) {
			void options.refetch();
		}
	};

	// `enabled` usually reads the polled resource itself, so without the memo
	// every response would restart the interval.
	const active = createMemo(options.enabled);

	createEffect(() => {
		if (!active() || hidden()) {
			return;
		}
		const interval = setInterval(tick, options.intervalMs);
		onCleanup(() => clearInterval(interval));
	});

	// Deferred so mounting in a visible tab does not fetch on top of the
	// caller's own initial load. The previous value is tracked here because
	// `on` does not record it for a deferred first run, which would skip the
	// refetch for a page that was opened in a background tab.
	let wasHidden = hidden();
	createEffect(
		on(
			hidden,
			(isHidden) => {
				const returned = wasHidden && !isHidden;
				wasHidden = isHidden;
				if (returned && active()) {
					tick();
				}
			},
			{ defer: true },
		),
	);
}
